<script setup lang="ts">
import VPageContainer from '@shared/layout/VPageContainer/VPageContainer.vue';
import { CrmRelationsRelatieContext } from '@services/crm-public/orval-api/model/crmRelationsRelatieContext';
import { SUREbusinessCommonDomainRelatiesSoortRelatieEnum } from '@services/crm-public/orval-api/model/sUREbusinessCommonDomainRelatiesSoortRelatieEnum';
import { useRelatieSpecialeContactpersonenMaakSpecialeContactpersonenAan } from '@services/crm-public/orval-api/relatie-speciale-contactpersonen-api/relatie-speciale-contactpersonen-api';
import { useRelatiesMaakRelatie } from '@services/crm-public/orval-api/relaties-api/relaties-api';
import { computed, provide, ref, toRefs, watch } from 'vue';
import { getSurenetUrl } from '@menu-lib/urlHelper';
import { useBreadcrumb } from '@menu-lib/breadcrumb/breadcrumb';
import { mapToDto } from '@relatie-lib/relatieGegevens/helper';
import {
  isParticuliereRelatie,
  isZakelijkeRelatie,
  type Relatie,
  type ZakelijkeRelatie,
} from '@relatie-lib/relatieGegevens/types';
import BestaandeRelatieBijKvkControle from '@relatie-lib/relatieGegevens/BestaandeRelatieBijKvkControle.vue';
import { isDisabledInjectionKey } from '@relatie-lib/relatieGegevens/relatiegegevensForm/keys';

import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import { useLocalStorage, useMagicKeys, whenever } from '@vueuse/core';
import { useClaims } from '@auth/useClaims';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { sleep } from '@tools/helpers';
import { useForm } from 'vee-validate';
import { useRouter } from 'vue-router';
import { useErrorHandler } from '@errors/useErrorHandler';
import ParticuliereRelatieForm from '../relaties/ParticuliereRelatieForm.vue';
import ZakelijkeRelatieForm from '../relaties/ZakelijkeRelatieForm.vue';
import RelatieAdviseurField from '../relaties/RelatieAdviseurField.vue';
import { useKvkBedrijfsProfiel } from './useKvkBedrijfsProfiel';

const props = withDefaults(
  defineProps<{
    soort: SUREbusinessCommonDomainRelatiesSoortRelatieEnum;
    returnTo?: 'risicoscan';
  }>(),
  {
    soort: SUREbusinessCommonDomainRelatiesSoortRelatieEnum.Z,
    returnTo: undefined,
  },
);

const { soort } = toRefs(props);

const keys = useMagicKeys();
const claims = useClaims();
const debug = useLocalStorage('CRM_APP_DEBUG', false);

whenever(keys.alt_w, () => {
  if (claims.value?.isInternal) debug.value = !debug.value;
});

const breadcrumbs = computed(() => {
  const url = '/app/crm/relatiegegevens';
  return [
    { link: getSurenetUrl(url, false), name: 'Relatiegegevens' },
    { name: 'Nieuwe relatie', link: '' },
  ];
});
useBreadcrumb(breadcrumbs);

const { handleError } = useErrorHandler();

const router = useRouter();
const notification = useNotification();

const editMode = ref<boolean>(true);
const loading = ref<boolean>(false);
const disabled = ref<boolean>(false);
const relatienummer = ref<number>();

const { values, handleSubmit, setFieldValue, errors } = useForm<Relatie>({
  initialValues: { typeName: soort.value },
});

const { bedrijfsProfiel } = useKvkBedrijfsProfiel(values);

watch(bedrijfsProfiel, (bedrijf) => {
  setFieldValue('adres.postcode', bedrijf?.adres.postcode);
  setFieldValue('adres.huisnummer', bedrijf?.adres.huisnummer);
  setFieldValue('adres.huisnummertoevoeging', bedrijf?.adres.huisnummertoevoeging);
  setFieldValue('bedrijfsnaam', bedrijf?.naam);
  setFieldValue('sbicode', bedrijf?.sbicode);
  setFieldValue('rechtsvorm', bedrijf?.rechtsvorm);
});

function onBestaandeZakelijkeRelatieSelected(bestaandeRelatienummer: number) {
  router.push({ name: 'Relatiegegevens', params: { relatienummer: bestaandeRelatienummer } });
}

function onError(err: any) {
  handleError(err);
  disabled.value = false;
}

const {
  mutate: maakSpecialeContactpersonenAan,
  isPending: isMuteerSpecialeContactpersonenPending,
} = useRelatieSpecialeContactpersonenMaakSpecialeContactpersonenAan({
  mutation: {
    onError,
    onSuccess,
  },
});

const zijnAfwijkendeEmailadressenAangepast = computed(() => !!values.financieelEmailadres);

async function onMaakRelatieAanSuccess(data: number) {
  relatienummer.value = data;

  if (zijnAfwijkendeEmailadressenAangepast.value) {
    maakSpecialeContactpersonenAan({
      relatienummer: relatienummer.value,
      data: {
        financieelEmailadres: values.financieelEmailadres,
      },
    });
  } else {
    await onSuccess();
  }
}

async function onSuccess() {
  notification.success(
    `Relatie ${isZakelijkeRelatie(values) ? values.bedrijfsnaam : isParticuliereRelatie(values) ? values.achternaam : ''} aangemaakt.`,
  );
  await sleep(1000);
  if (props.returnTo === 'risicoscan') {
    window.location.href = getSurenetUrl(
      `/net/riskinventarisation/selectcategories?relationID=${relatienummer.value}&reledit=1`,
      false,
    );
  } else {
    router.push({ name: 'Relatiegegevens', params: { relatienummer: relatienummer.value } });
  }
}

const { mutate: maakRelatieAan, isPending: isMaakRelatiePending } = useRelatiesMaakRelatie({
  mutation: {
    onError,
    onSuccess: onMaakRelatieAanSuccess,
  },
});
const isSubmitting = computed(
  () => isMaakRelatiePending.value || isMuteerSpecialeContactpersonenPending.value,
);

provide(isDisabledInjectionKey, { value: disabled });

const submit = handleSubmit(() => {
  disabled.value = true;
  maakRelatieAan({
    data: mapToDto(values, CrmRelationsRelatieContext.Algemeen),
  });
});
</script>
<template>
  <VPageContainer title="Nieuwe relatie">
    <VInfoBox class="mb-4" v-if="debug" badge-text="Debug: form values">
      <div class="whitespace-pre font-mono">{{ values }}</div>
    </VInfoBox>
    <VInfoBox class="mb-4" v-if="debug" badge-text="Debug: errors">
      <div class="whitespace-pre font-mono">{{ errors }}</div>
    </VInfoBox>
    <ZakelijkeRelatieForm
      v-if="props.soort === 'Z'"
      kan-opslaan
      :kan-bewerken="false"
      :toon-bedrijfseigenaar="false"
      :loading
      :is-submitting="isSubmitting"
      v-model="editMode"
      save-button-text="Relatie aanmaken"
      @save="submit()"
    >
      <RelatieAdviseurField v-if="claims?.isInternal" />
    </ZakelijkeRelatieForm>
    <BestaandeRelatieBijKvkControle
      v-if="props.soort === 'Z'"
      :kvk-nummer="(values as ZakelijkeRelatie).kvknummer ?? ''"
      @relatienummerSelected="onBestaandeZakelijkeRelatieSelected"
    />
    <ParticuliereRelatieForm
      v-if="props.soort === 'P'"
      kan-opslaan
      :kan-bewerken="false"
      :is-submitting="isSubmitting"
      :loading
      v-model="editMode"
      save-button-text="Relatie aanmaken"
      @save="submit()"
    >
      <RelatieAdviseurField v-if="claims?.isInternal" />
    </ParticuliereRelatieForm>
  </VPageContainer>
</template>
